<template>
  <WorldmcShell>
    <div class="worldmc-home">
      <section class="hero">
        <div class="hero-backdrop" />
        <div class="hero-content">
          <p class="hero-eyebrow">{{ t("shell.worldmc.home.eyebrow") }}</p>
          <h1>{{ t("shell.worldmc.home.title") }}</h1>
          <p class="hero-copy">{{ t("shell.worldmc.home.subtitle") }}</p>
        </div>
      </section>

      <section class="hotels-section">
        <div class="section-inner">
          <worldmc-featured-hotel
            v-for="cfg in featuredHotelsCfg"
            :key="cfg.hotelId"
            :hotel="cfg"
            @select="goHotelDetail"
          />
        </div>
      </section>

      <section class="more-stays">
        <div class="section-inner">
          <p class="more-title">{{ t("shell.worldmc.home.moreStays") }}</p>
          <div class="stays-grid">
            <worldmc-stay-card
              v-for="hotel in moreStaysHotels"
              :key="hotel._id"
              :hotel="hotel"
              @select="goHotelDetail"
            />
          </div>
          <button type="button" class="more-hotel" @click="goMoreHotel">
            {{ t("shell.worldmc.home.moreHotel") }} →
          </button>
        </div>
      </section>
    </div>
  </WorldmcShell>
</template>

<script setup>
import common from "@charmdeer/common";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { getUserSerializeAsToken } from "@/api/biz/user";
import { getRestCity } from "@/api/rest/common";
import WorldmcFeaturedHotel from "@/distributions/consumer/y/channels/worldmc/components/WorldmcFeaturedHotel.vue";
import WorldmcShell from "@/distributions/consumer/y/channels/worldmc/components/WorldmcShell.vue";
import WorldmcStayCard from "@/distributions/consumer/y/channels/worldmc/components/WorldmcStayCard.vue";
import { useYChannelStoreHook } from "@/distributions/consumer/y/store/channel";
import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";
import { useHotels } from "@/hooks/useHotels";
import { useUserStoreHook } from "@/store/modules/user";

const { t } = useI18n();
const router = useRouter();
const channelStore = useYChannelStoreHook();
const YHotelStore = useHotelStoreHook();
const userStore = useUserStoreHook();

const channelQuery = { channel: "worldmc" };

// 官方推荐酒店固定写死三家（北外滩清单，EUR 固定价，CDEH-63834），不再查接口
const featuredHotelsCfg = computed(
  () => channelStore.configs?.pages?.worldmc?.featuredHotels || []
);

// 首页酒店日期固定按大会日期：2027-03-30 入住、2027-04-02 离店
const homeCheckIn = "2027-03-30";
const homeCheckOut = "2027-04-02";
const homeStay = {
  date: { checkIn: homeCheckIn, checkOut: homeCheckOut },
  guest: { numberOfRooms: 1, numberOfAdults: 2, numberOfChildren: 0 }
};

// More Stays 不固定酒店，地点固定上海（复用渠道配置的上海城市 ref）查询
const shanghaiCityRef = computed(
  () => channelStore.configs?.pages?.worldmc?.carCityRef || ""
);
const { hotels: moreStaysHotels, fetchHotels: fetchMoreStaysHotels } =
  useHotels({
    stay: homeStay,
    filter: { city: { _id: shanghaiCityRef.value } },
    preferred: userStore.preferred,
    pageSize: 3
  });

// 跳转列表页需带地点 showLabel，预取上海城市数据按当前语言生成，取不到时回退渠道配置城市名
const shanghaiCityData = ref(null);
const shanghaiShowLabel = () =>
  (shanghaiCityData.value &&
    common.city.toString.name(shanghaiCityData.value, userStore.language)) ||
  channelStore.configs?.pages?.worldmc?.carCityName ||
  "";

onMounted(async () => {
  if (shanghaiCityRef.value) {
    fetchMoreStaysHotels();
    try {
      shanghaiCityData.value = await getRestCity(shanghaiCityRef.value);
    } catch (_error) {
      shanghaiCityData.value = null;
    }
  }
});

// 小屏（≤768px）酒店列表/详情由 m 站（ysys-m standard）承接：带渠道，登录态追加一次性 userToken
const isSmallScreen = () => window.matchMedia("(max-width: 768px)").matches;

const goMSiteIndex = async () => {
  const url = new URL(`${import.meta.env.VITE_M_SITE_URL}/index`);
  url.searchParams.set("channel", "worldmc");
  if (userStore.user?._id) {
    try {
      const token = await getUserSerializeAsToken();
      if (token) {
        url.searchParams.set("userToken", token);
      }
    } catch (_error) {
      // 取 token 失败不阻塞跳转，m 站按未登录处理
    }
  }
  window.location.href = url.href;
};

const goMoreHotel = () => {
  if (isSmallScreen()) {
    goMSiteIndex();
    return;
  }
  router.push({
    path: "/hotel/list",
    query: {
      ...channelQuery,
      locationType: "city",
      locationRef: shanghaiCityRef.value,
      showLabel: shanghaiShowLabel(),
      checkIn: homeCheckIn,
      checkOut: homeCheckOut,
      numberOfRooms: homeStay.guest.numberOfRooms,
      numberOfAdults: homeStay.guest.numberOfAdults,
      numberOfChildren: homeStay.guest.numberOfChildren
    }
  });
};

const goHotelDetail = hotel => {
  // 官方推荐酒店为渠道配置写死对象（hotelId），More Stays 为接口酒店（_id）
  const hotelId = hotel?._id || hotel?.hotelId;
  if (!hotelId) return;
  if (isSmallScreen()) {
    // 小屏酒店详情由 m 站承接（CDEH-63834）
    goMSiteIndex();
    return;
  }
  // 拷贝 stay，避免详情/列表页修改日期人数时反向改到首页查询参数
  YHotelStore.SET_SEARCH_MODEL({
    stay: {
      date: { ...homeStay.date },
      guest: { ...homeStay.guest }
    }
  });
  router.push({
    path: "/hotel/detail",
    query: {
      ...channelQuery,
      locationType: "city",
      locationRef: shanghaiCityRef.value,
      showLabel: shanghaiShowLabel(),
      hotelId,
      checkIn: homeCheckIn,
      checkOut: homeCheckOut,
      numberOfRooms: homeStay.guest.numberOfRooms,
      numberOfAdults: homeStay.guest.numberOfAdults,
      numberOfChildren: homeStay.guest.numberOfChildren
    }
  });
};

defineOptions({ name: "WorldmcHome" });
</script>

<style lang="scss" scoped>
.worldmc-home {
  width: 100%;
  color: #0d1f2b;
  background: #f6f9fa;
}

/* ---- Hero ---- */
.hero {
  position: relative;
  height: 480px;
  overflow: hidden;
  color: #fff;
}

.hero-backdrop {
  position: absolute;
  inset: 0;
  /* 蓝湖「酒店预订-2」Hero 切图（已含藏蓝可读性渐变），不再叠加 CSS 渐变 */
  background: url("../../../assets/worldmc/home-hero.jpg") center / cover;
}

.hero-content {
  position: relative;
  z-index: 1;
  /* KV 规格以签证页为标准模版（CDEH-63883）：eyebrow 14、h1 36、间距 16/14、padding-top 112 */
  max-width: 1440px;
  padding: 112px 72px 0;
  margin: 0 auto;
}

.hero-eyebrow {
  margin: 0 0 16px;
  font-family: "lato", sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-content h1 {
  margin: 0 0 14px;
  font-family: "Cormorant-Bold", "Cormorant", serif;
  font-size: 36px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
}

.hero-copy {
  max-width: 560px;
  margin: 0;
  color: rgba(255, 255, 255, 0.88);
  font-family: "lato", sans-serif;
  font-size: 15px;
  line-height: 1.6;
}

/* ---- Featured hotels ---- */
.hotels-section {
  padding: 0 0 8px;
  margin-top: -206px;
}

.section-inner {
  /* 内容宽 1296（CDEH-63844 二轮：1440 − 72×2），1440 = 1296 + 72×2 */
  max-width: 1440px;
  padding: 0 72px;
  margin: 0 auto;
}

/* ---- More stays ---- */
.more-stays {
  padding: 40px 0 60px;
  background: #f6f9fa;
}

.more-title {
  margin: 0 0 12px;
  color: #004c82;
  font-family: "lato", sans-serif;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.stays-grid {
  display: grid;
  /* minmax(0,1fr) 强制三列等宽：卡片内 nowrap 地址会撑大 auto 最小宽度，导致长地址列挤压其他列 */
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
}

.more-hotel {
  display: block;
  height: 36px;
  padding: 0 32px;
  margin: 40px auto 0;
  border: 1px solid #cdd9e2;
  color: #0a4486;
  background: #fff;
  cursor: pointer;
  font-family: "lato", sans-serif;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.more-hotel:hover {
  border-color: #0a4486;
}

/* ---- Responsive ---- */
@media screen and (max-width: 1280px) {
  .hero-content,
  .section-inner {
    padding-right: 36px;
    padding-left: 36px;
  }
}

@media screen and (max-width: 1000px) {
  .hotels-section {
    margin-top: -120px;
  }

  .stays-grid {
    /* minmax(0,1fr)：nowrap 地址的 min-content 会把 1fr 轨道撑破容器，小屏横向溢出 */
    grid-template-columns: minmax(0, 1fr);
  }
}

/* ---- Mobile（D-25）---- */
@media screen and (max-width: 768px) {
  .hero {
    height: 320px;
  }

  .hero-content {
    padding: 72px 24px 0;
  }

  /* KV 移动端规格同签证页（CDEH-63883）：eyebrow/副标题继承桌面值，h1 28 */
  .hero-content h1 {
    font-size: 28px;
  }

  .hotels-section {
    margin-top: -60px;
  }

  .section-inner {
    padding: 0 24px;
  }

  .more-stays {
    padding: 32px 0 40px;
  }

  .more-title {
    font-size: 15px;
  }

  /* 小屏 More Stays 左右滑动（CDEH-63820），与上方官方酒店的单列堆叠区分 */
  .stays-grid {
    display: flex;
    gap: 12px;
    margin: 0 -24px;
    padding: 0 24px;
    overflow-x: auto;
    scrollbar-width: none;
    scroll-snap-type: x mandatory;
  }

  .stays-grid::-webkit-scrollbar {
    display: none;
  }

  .stays-grid :deep(.stay-card) {
    flex: 0 0 78vw;
    scroll-snap-align: start;
  }

  .more-hotel {
    width: 100%;
  }
}
</style>
